import { ChangeDetectionStrategy, Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
    IMultiYearComparisonResponseDTO,
    IMultiYearNodeDTO,
} from '@modeso/types__aumico-lib-annualreport-be';
import { NavigationItem } from '../../../../../../aumico-components/src/lib';
import { hasNoNonZeroDescendantInYears } from '../models/tree-walk.util';

/**
 * Data passed when opening the modal. The harmonized DTO is the same one
 * that's already in the analytics NgRx store; the container just hands it
 * over so the modal can render the selectable tree without going back to
 * the store itself. `selectedYears` mirrors `ControlsState.selectedYears`
 * and is used to filter out branches that are zero across every selected
 * year — same rule as the on-screen view (PRD §3.5).
 */
export interface MultiYearExportModalData {
    harmonized: IMultiYearComparisonResponseDTO;
    selectedYears: string[];
    /**
     * Optional initial state from `Company.mycPreferences` — pre-fills the
     * Section-Toggles, Show-Accounts, Show-Ratios and tree-checkbox state
     * with the user's last-confirmed export config. `selectedNodeIds` has
     * already been reconciled against the live tree by the reducer, so we
     * can trust the ids exist here.
     */
    initialPreferences?: {
        includeBalance: boolean;
        includeIncome: boolean;
        showAccounts: boolean;
        showRatios: boolean;
        selectedNodeIds: string[];
    };
}

/**
 * Result returned via `MatDialogRef.close()`:
 *  - `{ ... }`: user confirmed → dispatch export
 *  - `null`: user cancelled (no export)
 */
export interface MultiYearExportModalResult {
    selectedNodeIds: string[];
    /** When true, the mapper emits `Acc` rows under every visible FLI / SUB_FLI. */
    showAccounts: boolean;
    /** When false, the mapper emits an empty BS section (skipped in template). */
    includeBalance: boolean;
    /** When false, the mapper emits an empty IS section (skipped in template). */
    includeIncome: boolean;
    /**
     * Modal override for the Verhältniskennzahl column. Decouples PDF from
     * the in-app `structMode` so the user can include / exclude ratios
     * independently of what's shown on screen.
     */
    showRatios: boolean;
}

/** Display kind used by the template to know how to indent / style each row. */
type RowKind = 'section' | 'group' | 'fli' | 'subFli' | 'subTotal';

interface TreeRow {
    id: string;
    label: string;
    kind: RowKind;
    /** 0 = section root, 1 = group, 2 = FLI, 3+ = sub-fli. Drives the indent. */
    level: number;
    /** Which tab this row belongs to. */
    section: 'BALANCE' | 'INCOME';
}

/**
 * Modal opened from the Mehrjahresvergleich page when the user clicks
 * "Export PDF".
 *
 * Layout: two-column.
 *   - Left column: `ham-tab-navigation` (Bilanz / Erfolgsrechnung) + a
 *     scrollable tree of selectable nodes. The tab purely controls which
 *     subset of `rows` is rendered — selection state is shared.
 *   - Right column: two `ham-accordion`s.
 *       * "Bestandteile" — `Bilanz` and `Erfolgsrechnung` section toggles.
 *         Switching one OFF visually disables the corresponding tab on the
 *         left but preserves checkbox state (re-enabling restores it 1:1).
 *       * "Weitere Einstellungen" — `Konten anzeigen` global toggle. Mirrors
 *         the print-options accordion idiom.
 *
 * Defaults:
 *   - Section toggles ON (Bilanz + ER both included).
 *   - Tree checkboxes ALL OFF — user opts in to what they want exported.
 *   - `showAccounts` OFF.
 *
 * Behaviour:
 *   - Strict per-row checkbox toggling, no cascade.
 *   - Export button is enabled iff at least one tree checkbox in an
 *     *enabled* section is ticked.
 */
@Component({
    selector: 'multi-year-export-modal',
    templateUrl: './multi-year-export-modal.component.html',
    styleUrls: ['./multi-year-export-modal.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MultiYearExportModalComponent {
    /** All rows for both sections — split per section for rendering via getters. */
    public readonly rows: TreeRow[];

    /** Per-id checkbox state. Strict per-row — no cascade. */
    public selected: Set<string> = new Set();

    /** Right-side "Bestandteile" toggles. Default ON. */
    public includeBalance = true;
    public includeIncome = true;

    /** Right-side "Einstellungen" → "Konten anzeigen". Default OFF. */
    public showAccounts = false;

    /**
     * Right-side "Einstellungen" → "Verhältniskennzahlen". Default OFF.
     * When ON, the mapper emits ratio columns (`col{i}P`) regardless of the
     * in-app `structMode`. Independent control over the PDF.
     */
    public showRatios = false;

    // ─── Tab nav (left, top) ────────────────────────────────────────────────
    // Mirrors `ham-tab-navigation` usage from `grouping-masking.component.*`.

    public navigationItems: NavigationItem[] = [
        { name: 'aumico_analytics_controls_scope_balance', active: true },
        { name: 'aumico_analytics_controls_scope_income', active: false },
    ];
    public tabsTitles: string[] = ['Bilanz', 'Erfolgsrechnung'];
    public selectedTab: 0 | 1 = 0;

    // ─── Accordion state (right rail) ───────────────────────────────────────
    // Mirrors the print-options pattern (aumico-lib-annualreport-fe):
    //
    //   * `accordionInitialOpen` is a CONSTANT bound to `[isOpened]`. It only
    //     drives the initial `_expanded` of the accordion-item and is never
    //     mutated — that avoids the init-loop where a `(opened)` emit on
    //     mount would flip the same state that's bound to `[isOpened]`.
    //   * `isComponentsAccordionExpanded` / `isMoreSettingsAccordionExpanded`
    //     track the live expanded state for the chevron icon swap. They're
    //     SET (not flipped) by `(opened)` / `(closed)` so they always agree
    //     with whatever the accordion-item just transitioned to. No coupling
    //     back into `[isOpened]` → no oscillation.
    //
    // Both accordions start expanded — that's what the user wants visually
    // and `accordionInitialOpen = true` is the one knob that controls it.
    public readonly accordionInitialOpen = true;
    public isComponentsAccordionExpanded = true;
    public isMoreSettingsAccordionExpanded = true;

    constructor(
        @Inject(MAT_DIALOG_DATA) public readonly data: MultiYearExportModalData,
        private readonly dialogRef: MatDialogRef<MultiYearExportModalComponent, MultiYearExportModalResult>,
    ) {
        const selectedYearIndices = this.computeSelectedYearIndices(data.harmonized.yearLabels, data.selectedYears);
        this.rows = this.buildRows(data.harmonized, selectedYearIndices);

        // Hydrate the modal state from persisted preferences when available.
        // The container reads `Company.mycPreferences` from the store and
        // hands the relevant subset in via `data.initialPreferences`. Stale
        // node ids are already filtered out by the reducer at load-time
        // against the live harmonized tree, so we can trust them here.
        const init = data.initialPreferences;
        if (init != null) {
            this.includeBalance = init.includeBalance;
            this.includeIncome = init.includeIncome;
            this.showAccounts = init.showAccounts;
            this.showRatios = init.showRatios;
            this.selected = new Set(init.selectedNodeIds);
        }
    }

    // ─── Tree building ──────────────────────────────────────────────────────

    private computeSelectedYearIndices(yearLabels: string[], selectedYears: string[]): number[] {
        const set = new Set(selectedYears);
        const out: number[] = [];
        yearLabels.forEach((y, idx) => { if (set.has(y)) out.push(idx); });
        return out;
    }

    /**
     * Walk each section root and emit one `TreeRow` per visible node. Mirrors
     * the on-screen view's filter rule: a node is hidden when it AND every
     * descendant are zero across the selected years. ACCOUNT nodes are skipped
     * — they're toggled globally via the "Show accounts" switch.
     */
    private buildRows(
        harmonized: IMultiYearComparisonResponseDTO,
        selectedYearIndices: number[],
    ): TreeRow[] {
        const rows: TreeRow[] = [];

        const visit = (
            node: IMultiYearNodeDTO,
            level: number,
            section: 'BALANCE' | 'INCOME',
        ): void => {
            if (node.type === 'ACCOUNT') return;

            if (selectedYearIndices.length > 0 && hasNoNonZeroDescendantInYears(node, selectedYearIndices)) {
                return;
            }

            const kind: RowKind = node.type === 'GROUP'
                ? (level === 0 ? 'section' : 'group')
                : node.type === 'FLI'
                    ? 'fli'
                    : node.type === 'SUB_TOTAL'
                        ? 'subTotal'
                        : 'subFli';

            rows.push({ id: node.id, label: node.label, kind, level, section });

            for (const child of node.children) {
                visit(child, level + 1, section);
            }
        };

        if (harmonized.assets) visit(harmonized.assets, 0, 'BALANCE');
        if (harmonized.liabilities) visit(harmonized.liabilities, 0, 'BALANCE');
        if (harmonized.incomeStatement) visit(harmonized.incomeStatement, 0, 'INCOME');

        return rows;
    }

    // ─── Tab nav ────────────────────────────────────────────────────────────

    /**
     * Switching tabs is purely visual — the underlying selection set isn't
     * touched. Disabled sections still allow tab clicks so the user can see
     * (greyed-out) what's there; only checkbox interaction is suppressed.
     */
    public onTabClick(item: NavigationItem): void {
        const index = this.navigationItems.findIndex((nav) => nav.name === item.name);
        if (index === -1) return;
        this.navigationItems = this.navigationItems.map((nav, i) => ({ ...nav, active: i === index }));
        this.selectedTab = index as 0 | 1;
    }

    public get visibleRows(): TreeRow[] {
        const section = this.selectedTab === 0 ? 'BALANCE' : 'INCOME';
        return this.rows.filter((r) => r.section === section);
    }

    /**
     * True when the *currently visible* tab's section is switched off in the
     * "Bestandteile" accordion. Drives the disabled overlay on the tree.
     */
    public get currentTabDisabled(): boolean {
        return this.selectedTab === 0 ? !this.includeBalance : !this.includeIncome;
    }

    // ─── Checkbox state + toggling ──────────────────────────────────────────

    public isChecked(row: TreeRow): boolean {
        return this.selected.has(row.id);
    }

    public onToggle(row: TreeRow): void {
        if (this.currentTabDisabled) return;
        const next = new Set(this.selected);
        if (next.has(row.id)) {
            next.delete(row.id);
        } else {
            next.add(row.id);
        }
        this.selected = next;
    }

    // ─── Quick-select actions (active tab scoped) ──────────────────────────
    //
    // All quick-actions operate ONLY on the currently visible tab. That keeps
    // the mental model crisp: what you see in the tree is what gets touched;
    // the other tab is never silently mutated.

    /** True when every row of the active tab is already in `selected`. */
    public get currentTabAllSelected(): boolean {
        const tabRows = this.visibleRows;
        if (tabRows.length === 0) return false;
        return tabRows.every((r) => this.selected.has(r.id));
    }

    /**
     * Smart toggle: if every row of the active tab is selected → clear them
     * all; otherwise → add them all. Two states, one button, label swaps via
     * `currentTabAllSelected` in the template.
     */
    public onToggleSelectAllInActiveTab(): void {
        if (this.currentTabDisabled) return;
        const tabRows = this.visibleRows;
        const next = new Set(this.selected);
        if (this.currentTabAllSelected) {
            for (const r of tabRows) next.delete(r.id);
        } else {
            for (const r of tabRows) next.add(r.id);
        }
        this.selected = next;
    }

    /**
     * Smart toggle scoped to group / section rows ONLY — other row kinds
     * (FLI / SubRow / SubTotal) are never touched.
     *
     *   - If every group/section row in the active tab is already selected
     *     → REMOVE them all (selection of other kinds stays intact)
     *   - Else → ADD all group/section rows to the existing selection
     *
     * Label stays "Alle Gruppen" in both cases — only the action toggles.
     */
    public onSelectGroupsInActiveTab(): void {
        if (this.currentTabDisabled) return;
        const groupRows = this.currentTabGroupRows;
        if (groupRows.length === 0) return;
        const next = new Set(this.selected);
        if (this.allOfKindSelected(groupRows)) {
            for (const r of groupRows) next.delete(r.id);
        } else {
            for (const r of groupRows) next.add(r.id);
        }
        this.selected = next;
    }

    /**
     * Smart toggle scoped to SUB_TOTAL rows ONLY (Bruttogewinn /
     * Betriebsergebnis / etc.) — other row kinds are never touched.
     *
     *   - If every subtotal row in the active tab is already selected
     *     → REMOVE them all
     *   - Else → ADD all subtotal rows to the existing selection
     *
     * Label stays "Alle Zwischensummen" in both cases.
     */
    public onSelectSubTotalsInActiveTab(): void {
        if (this.currentTabDisabled) return;
        const subTotalRows = this.currentTabSubTotalRows;
        if (subTotalRows.length === 0) return;
        const next = new Set(this.selected);
        if (this.allOfKindSelected(subTotalRows)) {
            for (const r of subTotalRows) next.delete(r.id);
        } else {
            for (const r of subTotalRows) next.add(r.id);
        }
        this.selected = next;
    }

    /**
     * Subtotals shortcut only renders on the ER tab — Bilanz has no SUB_TOTAL
     * nodes (Bruttogewinn / Betriebsergebnis live in the income statement).
     * Also hidden when the ER tab has no subtotal rows (nothing to act on).
     */
    public get showSubTotalsShortcut(): boolean {
        return this.selectedTab === 1 && this.currentTabSubTotalRows.length > 0;
    }

    private get currentTabGroupRows(): TreeRow[] {
        return this.visibleRows.filter((r) => r.kind === 'section' || r.kind === 'group');
    }

    private get currentTabSubTotalRows(): TreeRow[] {
        return this.visibleRows.filter((r) => r.kind === 'subTotal');
    }

    private allOfKindSelected(rows: TreeRow[]): boolean {
        if (rows.length === 0) return false;
        return rows.every((r) => this.selected.has(r.id));
    }

    // ─── Right-side toggles ─────────────────────────────────────────────────

    public onToggleIncludeBalance(value: boolean): void {
        this.includeBalance = value;
    }

    public onToggleIncludeIncome(value: boolean): void {
        this.includeIncome = value;
    }

    public onToggleShowAccounts(value: boolean): void {
        this.showAccounts = value;
    }

    public onToggleShowRatios(value: boolean): void {
        this.showRatios = value;
    }

    // Setters wired to (opened)/(closed). They SET, not FLIP — fed the
    // accordion-item's new state directly. Decoupled from `[isOpened]` so
    // there's no init-loop or feedback into the bound input.
    public onComponentsAccordionOpened(): void {
        this.isComponentsAccordionExpanded = true;
    }
    public onComponentsAccordionClosed(): void {
        this.isComponentsAccordionExpanded = false;
    }
    public onMoreSettingsAccordionOpened(): void {
        this.isMoreSettingsAccordionExpanded = true;
    }
    public onMoreSettingsAccordionClosed(): void {
        this.isMoreSettingsAccordionExpanded = false;
    }

    // ─── Footer actions ─────────────────────────────────────────────────────

    public onCancel(): void {
        this.dialogRef.close(null);
    }

    public onConfirm(): void {
        if (this.exportDisabled) return;
        this.dialogRef.close({
            selectedNodeIds: Array.from(this.selected),
            showAccounts: this.showAccounts,
            includeBalance: this.includeBalance,
            includeIncome: this.includeIncome,
            showRatios: this.showRatios,
        });
    }

    /**
     * Disabled when there's no enabled section with at least one ticked
     * checkbox. A checkbox in an OFF section doesn't count — that's the
     * "state preserved but ignored" contract the user asked for.
     */
    public get exportDisabled(): boolean {
        if (!this.includeBalance && !this.includeIncome) return true;
        const hasBs = this.includeBalance && this.rows.some((r) => r.section === 'BALANCE' && this.selected.has(r.id));
        const hasIs = this.includeIncome && this.rows.some((r) => r.section === 'INCOME' && this.selected.has(r.id));
        return !hasBs && !hasIs;
    }
}
